/**
 * @file Ordering list board, compact controls, and closed-history composition.
 */

import { appData } from "../../app/state/state.js";
import { applyBrandingVariables } from "../../shared/ui/brandingTokens.js";
import { escapeHTML } from "../../shared/utils/contentUtils.js";
import { queueOrderingStatusChange } from "./orderingMutations.js";
import {
    openOrderingAddModal,
    openOrderingItemModal,
} from "./orderingModal.js";
import { renderOrderingCard } from "./orderingCards.js";
import { loadNextOrderingClosedPage } from "./orderingListeners.js";
import {
    ACTIVE_ITEM_LIMIT,
    getClosedBucketState,
    getOrderingState,
} from "./orderingState.js";
import {
    compareActiveOrderingItems,
    compareClosedOrderingItems,
} from "./orderingSort.js";

let focusRestorationToken = 0;
const FOCUS_NAVIGATION_KEYS = new Set([
    "Tab",
    "ArrowUp",
    "ArrowDown",
    "ArrowLeft",
    "ArrowRight",
    "Home",
    "End",
    "PageUp",
    "PageDown",
]);

function invalidateBoardFocusIntent() {
    focusRestorationToken += 1;
}

document.addEventListener("pointerdown", invalidateBoardFocusIntent, true);
document.addEventListener("focusin", invalidateBoardFocusIntent, true);
document.addEventListener("keydown", (event) => {
    if (FOCUS_NAVIGATION_KEYS.has(event.key)) {invalidateBoardFocusIntent();}
}, true);

function userLabel(uid) {
    const user = appData.users.find((entry) => (entry.id || entry.uid) === uid);
    return user?.name || user?.displayName || user?.email || "";
}

function itemMatchesSearch(item, bucketName, search) {
    if (!search) {return true;}
    return [
        item.name,
        item.details,
        item.notesSearchText,
        userLabel(item.createdBy),
        userLabel(item.updatedBy),
        bucketName,
    ].some((value) => String(value || "").toLocaleLowerCase().includes(search));
}

function renderSearchControl(search) {
    return `
        <div class="flex h-10 min-w-0 flex-1 items-center rounded-full border border-gray-200 bg-white px-3 text-gray-500 transition-colors sm:w-[17rem] sm:flex-none sm:px-4" data-search-wrapper>
            <span class="material-symbols-outlined mr-2 text-[20px] text-gray-500" data-search-icon>search</span>
            <input type="text" class="board-search-input ordering-search-input min-w-0 flex-1 truncate bg-transparent text-sm text-gray-700 outline-none placeholder:truncate placeholder:text-gray-400" value="${escapeHTML(search)}" placeholder="Search this board" aria-label="Search Ordering">
            <button type="button" class="ordering-search-clear ${search ? "" : "hidden"} inline-flex shrink-0 items-center justify-center self-center text-gray-400 transition-colors hover:text-[var(--dfc-brand-primary)]" aria-label="Clear Ordering search" ${search ? "" : "disabled aria-hidden=\"true\""}>
                <span class="material-symbols-outlined block text-[18px] leading-none">close</span>
            </button>
        </div>
    `;
}

function renderFooter(checked) {
    return `
        <div class="mt-6 flex flex-wrap items-center justify-end gap-x-6 gap-y-3">
            <div class="flex items-center gap-3">
                <span class="text-sm font-medium text-gray-700">Show Closed</span>
                <label class="board-footer-switch shrink-0">
                    <input type="checkbox" class="ordering-closed-toggle" aria-label="Show Closed" ${checked ? "checked" : ""}>
                    <span class="board-footer-switch-track"></span>
                </label>
            </div>
        </div>
    `;
}

function renderBucket(bucket, search, showClosed) {
    const ordering = getOrderingState();
    const active = ordering.activeItems
        .filter((item) => item.listId === bucket.id)
        .filter((item) => itemMatchesSearch(item, bucket.name, search))
        .sort(compareActiveOrderingItems);
    const closedPage = getClosedBucketState(bucket.id);
    const closed = closedPage.items
        .filter((item) => itemMatchesSearch(item, bucket.name, search))
        .sort(compareClosedOrderingItems);
    const noActiveResults = active.length === 0;
    const showClosedSection = showClosed && (
        closed.length > 0 ||
        closedPage.loading ||
        closedPage.error ||
        !closedPage.loaded
    );
    return `
        <section class="ordering-bucket kanban-column group min-w-0 rounded-lg bg-white p-4 shadow" data-ordering-bucket-id="${escapeHTML(bucket.id)}">
            <header class="mb-4 flex items-start justify-between gap-2">
                <h3 class="min-w-0 flex-1 overflow-hidden break-words text-lg font-bold leading-6 text-gray-800 [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]" title="${escapeHTML(bucket.name)}">${escapeHTML(bucket.name)}</h3>
                <button type="button" class="ordering-bucket-quick-add quick-add-task-btn kanban-card-action-btn shrink-0 opacity-100 focus:opacity-100 sm:opacity-0 sm:group-hover:opacity-100" data-ordering-quick-add="${escapeHTML(bucket.id)}" title="Add item to ${escapeHTML(bucket.name)}" aria-label="Add item to ${escapeHTML(bucket.name)}">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" /></svg>
                </button>
            </header>
            <div class="space-y-3" data-ordering-active-list>
                ${active.map(renderOrderingCard).join("")}
            </div>
            ${showClosedSection ? `
                <div class="mt-4 border-t border-gray-200 pt-3">
                    ${closed.length ? `<h4 class="text-xs font-semibold uppercase tracking-wide text-gray-500">Closed <span class="font-normal text-gray-400">${closed.length}</span></h4>` : ""}
                    <div class="mt-2.5 space-y-2.5">${closed.map(renderOrderingCard).join("")}</div>
                    ${closedPage.error ? `<p class="mt-3 text-sm text-red-600">Closed items could not be loaded. Try again.</p>` : ""}
                    ${!closedPage.loaded && !closedPage.error ? `<p class="mt-3 text-sm text-gray-500">Loading closed items...</p>` : ""}
                    ${closedPage.loaded && closedPage.hasMore ? `<button type="button" class="ordering-load-more mt-3 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-[var(--dfc-brand-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500/60 aria-disabled:pointer-events-none aria-disabled:opacity-50" data-ordering-load-more="${escapeHTML(bucket.id)}" aria-disabled="${closedPage.loading}">${closedPage.loading ? "Loading..." : "Load more"}</button>` : ""}
                </div>
            ` : ""}
            ${noActiveResults && (!showClosed || closed.length === 0) ? `<p class="ordering-bucket-empty mt-2 text-center text-sm text-gray-400">${search ? "No matching items." : "No items"}</p>` : ""}
        </section>
    `;
}

function renderControls(search) {
    return `
        <div class="board-controls-shell flex items-center gap-2 sm:flex-row sm:flex-wrap sm:items-start sm:justify-end">
            ${renderSearchControl(search)}
            <div class="flex shrink-0 items-center justify-end gap-1.5 sm:gap-2">
                <button type="button" class="ordering-add-item inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--dfc-brand-primary)] text-[var(--dfc-brand-primary-contrast)] shadow-sm transition-colors hover:bg-[var(--dfc-brand-primary-hover)] focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500/60 focus-visible:ring-offset-2" aria-label="Add Ordering item" title="Add Ordering item">
                    <span class="material-symbols-outlined pointer-events-none text-[22px]">add</span>
                </button>
            </div>
        </div>
    `;
}

function errorMessage(error) {
    return escapeHTML(error?.message || "The Ordering data listener failed. Try navigating away and back.");
}

function restoreBoardFocus(identity, token = ++focusRestorationToken) {
    requestAnimationFrame(() => {
        if (token !== focusRestorationToken) {return;}
        let target = null;
        if (identity?.kind === "search") {
            target = document.querySelector(".ordering-search-input");
        } else if (identity?.kind === "search-clear") {
            target = document.querySelector(".ordering-search-clear:not([disabled])");
        } else if (identity?.kind === "add-item") {
            target = document.querySelector(".ordering-add-item");
        } else if (identity?.kind === "bucket-add" && identity.listId) {
            target = document.querySelector(
                `[data-ordering-bucket-id="${CSS.escape(identity.listId)}"] ` +
                ".ordering-bucket-quick-add",
            );
        } else if (identity?.kind === "closed-toggle") {
            target = document.querySelector(".ordering-closed-toggle");
        } else if (identity?.kind === "load-more" && identity.listId) {
            target = document.querySelector(
                `[data-ordering-bucket-id="${CSS.escape(identity.listId)}"] ` +
                ".ordering-load-more",
            );
        } else if (identity?.kind === "status-action" && identity.itemId) {
            const itemSelector = `[data-ordering-item-id="${CSS.escape(identity.itemId)}"]`;
            if (identity.targetStatus) {
                target = document.querySelector(
                    `${itemSelector} .ordering-status-action` +
                    `[data-target-status="${CSS.escape(identity.targetStatus)}"]`,
                );
            }
            target ||= document.querySelector(`${itemSelector} .ordering-card-open`);
        } else if (identity?.kind === "item" && identity.itemId) {
            target = document.querySelector(
                `[data-ordering-item-id="${CSS.escape(identity.itemId)}"] ` +
                ".ordering-card-open",
            );
        }
        target ||= document.querySelector(".ordering-closed-toggle") ||
            document.querySelector(".ordering-add-item");
        if (!target?.isConnected || target.getClientRects().length === 0 ||
            target.closest('[aria-hidden="true"]') || target.closest("[inert]")) {
            return;
        }
        if (target) {
            target.dataset.orderingRestoredFocus = "true";
            target.addEventListener("pointerdown", () => {
                delete target.dataset.orderingRestoredFocus;
            }, {once: true});
        }
        target?.focus({preventScroll: true});
    });
}

function captureFocusedBoardIdentity(controlsRoot, root) {
    const active = document.activeElement;
    if (!active || (!controlsRoot.contains(active) && !root.contains(active))) {return null;}
    if (active.matches(".ordering-search-input")) {return {kind: "search"};}
    if (!active.matches(":focus-visible") &&
        active.dataset.orderingRestoredFocus !== "true") {return null;}
    if (active.matches(".ordering-search-clear")) {return {kind: "search-clear"};}
    if (active.matches(".ordering-add-item")) {return {kind: "add-item"};}
    if (active.matches(".ordering-closed-toggle")) {return {kind: "closed-toggle"};}
    const itemId = active.closest("[data-ordering-item-id]")?.dataset.orderingItemId;
    if (active.matches(".ordering-status-action") && itemId) {
        return {
            kind: "status-action",
            itemId,
            targetStatus: active.dataset.targetStatus,
        };
    }
    if (active.matches(".ordering-card-open") && itemId) {
        return {kind: "item", itemId};
    }
    const listId = active.closest("[data-ordering-bucket-id]")?.dataset.orderingBucketId;
    if (active.matches(".ordering-load-more") && listId) {
        return {kind: "load-more", listId};
    }
    return null;
}

function keyboardFocusIdentity(event, identity) {
    const trigger = event.currentTarget;
    const keyboardTriggered = trigger.dataset.orderingKeyboardIntent === "true" ||
        event.detail === 0 || trigger.matches(":focus-visible");
    delete trigger.dataset.orderingKeyboardIntent;
    focusRestorationToken += 1;
    return keyboardTriggered && document.activeElement === trigger ? {
        identity,
        token: focusRestorationToken,
    } : null;
}

function bindKeyboardFocusIntent(trigger) {
    trigger?.addEventListener("keydown", (event) => {
        if (event.key === "Enter" || event.key === " ") {
            trigger.dataset.orderingKeyboardIntent = "true";
        }
    });
    trigger?.addEventListener("pointerdown", () => {
        delete trigger.dataset.orderingKeyboardIntent;
    });
}

function renderWithFocus(generation, focusRequest) {
    renderOrderingBoard(generation);
    if (focusRequest?.token === focusRestorationToken) {
        restoreBoardFocus(focusRequest.identity, focusRequest.token);
    }
}

/**
 * Renders the current Ordering route state and binds board interactions.
 * @param {number} generation Current route generation.
 * @returns {void}
 */
export function renderOrderingBoard(generation) {
    const root = document.getElementById("ordering-board");
    const controlsRoot = document.getElementById("ordering-board-controls");
    const footerRoot = document.getElementById("ordering-board-footer");
    const view = document.getElementById("ordering-view");
    if (!root || !controlsRoot || !footerRoot || !view) {return;}
    const retainedFocus = captureFocusedBoardIdentity(controlsRoot, root);
    applyBrandingVariables(view, appData);
    const ordering = getOrderingState();
    const loading = ordering.bucketLoadState === "loading" ||
        ordering.activeLoadState === "loading";
    if (loading) {
        controlsRoot.replaceChildren();
        footerRoot.replaceChildren();
        root.innerHTML = `<div class="rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-500" role="status">Loading Ordering...</div>`;
        return;
    }
    if (ordering.bucketError || ordering.activeError) {
        controlsRoot.replaceChildren();
        footerRoot.replaceChildren();
        root.innerHTML = `<div class="rounded-lg border border-red-200 bg-red-50 p-6"><h3 class="font-semibold text-red-800">Ordering could not be loaded</h3><p class="mt-1 text-sm text-red-700">${errorMessage(ordering.bucketError || ordering.activeError)}</p></div>`;
        return;
    }
    if (ordering.buckets.length === 0) {
        controlsRoot.replaceChildren();
        footerRoot.replaceChildren();
        root.innerHTML = `<p class="rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center text-sm text-gray-500">No Ordering lists are configured. Contact an administrator.</p>`;
        return;
    }

    const search = ordering.search.trim().toLocaleLowerCase();
    controlsRoot.innerHTML = renderControls(ordering.search);
    footerRoot.innerHTML = renderFooter(ordering.showClosed);
    root.innerHTML = `
        ${ordering.activeItems.length >= ACTIVE_ITEM_LIMIT ? `<p class="mb-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">Showing the newest 1,000 active items. Search applies to this loaded set.</p>` : ""}
        <div class="ordering-buckets grid w-full min-w-0 grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4" aria-label="Ordering lists">
            ${ordering.buckets.map((bucket) => renderBucket(bucket, search, ordering.showClosed)).join("")}
        </div>
    `;

    controlsRoot.querySelector(".ordering-search-input")?.addEventListener("input", (event) => {
        focusRestorationToken += 1;
        ordering.search = event.target.value;
        const selectionStart = event.target.selectionStart;
        renderOrderingBoard(generation);
        const nextInput = controlsRoot.querySelector(".ordering-search-input");
        nextInput?.focus();
        if (selectionStart !== null) {
            nextInput?.setSelectionRange(selectionStart, selectionStart);
        }
    });
    controlsRoot.querySelector(".ordering-search-clear")?.addEventListener("click", () => {
        focusRestorationToken += 1;
        ordering.search = "";
        renderOrderingBoard(generation);
        controlsRoot.querySelector(".ordering-search-input")?.focus();
    });
    const closedToggle = footerRoot.querySelector(".ordering-closed-toggle");
    bindKeyboardFocusIntent(closedToggle);
    closedToggle?.addEventListener("change", (event) => {
        const focusRequest = keyboardFocusIdentity(event, {kind: "closed-toggle"});
        ordering.showClosed = event.target.checked;
        renderWithFocus(generation, focusRequest);
        if (ordering.showClosed) {
            ordering.buckets.forEach((bucket) => {
                const page = getClosedBucketState(bucket.id);
                if (!page.loaded && !page.loading) {
                    void loadNextOrderingClosedPage(
                        bucket.id,
                        generation,
                        () => renderWithFocus(generation, focusRequest),
                    );
                }
            });
        }
    });
    controlsRoot.querySelector(".ordering-add-item")?.addEventListener("click", () => {
        focusRestorationToken += 1;
        openOrderingAddModal({
            onRender: () => renderOrderingBoard(generation),
            onFocusRestore: restoreBoardFocus,
        });
    });
    root.querySelectorAll(".ordering-bucket-quick-add").forEach((button) => {
        button.addEventListener("click", () => {
            focusRestorationToken += 1;
            openOrderingAddModal({
                initialListId: button.dataset.orderingQuickAdd,
                focusIdentity: {
                    kind: "bucket-add",
                    listId: button.dataset.orderingQuickAdd,
                },
                onRender: () => renderOrderingBoard(generation),
                onFocusRestore: restoreBoardFocus,
            });
        });
    });
    root.querySelectorAll(".ordering-load-more").forEach((button) => {
        bindKeyboardFocusIntent(button);
        button.addEventListener("click", (event) => {
            const listId = button.closest("[data-ordering-bucket-id]")?.dataset.orderingBucketId;
            if (listId) {
                const focusRequest = keyboardFocusIdentity(event, {
                    kind: "load-more",
                    listId,
                });
                void loadNextOrderingClosedPage(listId, generation, () =>
                    renderWithFocus(generation, focusRequest));
            }
        });
    });
    root.querySelectorAll(".ordering-status-action").forEach((button) => {
        bindKeyboardFocusIntent(button);
        button.addEventListener("click", (event) => {
            event.stopPropagation();
            const itemId = button.closest("[data-ordering-item-id]")?.dataset.orderingItemId;
            const targetStatus = button.dataset.targetStatus;
            if (itemId && targetStatus) {
                const focusRequest = keyboardFocusIdentity(event, {
                    kind: "status-action",
                    itemId,
                    targetStatus,
                });
                void queueOrderingStatusChange(
                    itemId,
                    targetStatus,
                    () => renderWithFocus(generation, focusRequest),
                );
            }
        });
    });
    root.querySelectorAll(".ordering-card").forEach((card) => {
        if (card.dataset.orderingPendingCreate === "true") {return;}
        const open = () => {
            focusRestorationToken += 1;
            const item = getOrderingState().activeItems
                .find((entry) => entry.id === card.dataset.orderingItemId) ||
                Array.from(getOrderingState().closedByBucket.values())
                    .flatMap((page) => page.items)
                    .find((entry) => entry.id === card.dataset.orderingItemId);
            if (item) {
                openOrderingItemModal(item, {
                    onRender: () => renderOrderingBoard(generation),
                    onFocusRestore: restoreBoardFocus,
                });
            }
        };
        card.addEventListener("click", (event) => {
            if (event.target instanceof Element &&
                event.target.closest("[data-ordering-status-actions]")) {return;}
            open();
        });
    });
    if (ordering.showClosed) {
        ordering.buckets.forEach((bucket) => {
            const page = getClosedBucketState(bucket.id);
            if (!page.loaded && !page.loading) {
                void loadNextOrderingClosedPage(bucket.id, generation, () =>
                    renderOrderingBoard(generation));
            }
        });
    }
    if (retainedFocus) {restoreBoardFocus(retainedFocus);}
}
